import type {
  IAddressParkingValues,
  IBasicInformationValues,
  IInsuranceSoftwareValues,
  IShiftDetailsValues,
  ILocationSectionValues,
  TLocationSectionId,
} from '../types/LocationSettings';

/**
 * Whether a required field lets Save through: one the office has not touched
 * never blocks saving an unrelated edit, even when it is empty in the record.
 */
export const requiredOk = (isTouched: boolean | undefined, isValid: boolean): boolean =>
  !isTouched || isValid;

/** A required text answer: anything but blanks. */
export const isFilled = (value: string | null | undefined): boolean => !!value?.trim();

/** `useOfficeProfile.js`, minus the two contact names the split drops. */
export const isBasicInformationValid = (values: IBasicInformationValues): boolean =>
  isFilled(values.officeName) && isFilled(values.officeType) && isFilled(values.specialty);

/** `useParking.js`: answered, a cost and unit when paid, and a parking type. */
const isParkingCostAnswered = (values: IAddressParkingValues): boolean =>
  values.isParkingFree !== undefined &&
  (values.isParkingFree || (!!values.parkingCost && !!values.parkingCostType));

export const isAddressParkingValid = (values: IAddressParkingValues): boolean =>
  isParkingCostAnswered(values) && !!values.parkingType;

const TIME_FIELDS = ['timePerPatientRDH', 'timePerPatientDA', 'timePerPatientDN'] as const;

/** `useOfficeInformation.js`: every role answered, and at least one not `'N/A'`. */
export const isTimePerPatientValid = (values: IShiftDetailsValues): boolean =>
  TIME_FIELDS.every((key) => values[key] !== '') &&
  TIME_FIELDS.some((key) => values[key] !== 'N/A');

/** `useOfficeInformation.js` treats only `undefined` as unanswered. */
export const isAssistedHygieneAnswered = (values: IShiftDetailsValues): boolean =>
  values.isAssistedHygiene !== undefined;

export const isShiftDetailsValid = (values: IShiftDetailsValues): boolean =>
  isTimePerPatientValid(values) && isAssistedHygieneAnswered(values);

/** `useSoftware.js` and `useInsuranceProvider.js`: neither list may be empty. */
export const isInsuranceSoftwareValid = (values: IInsuranceSoftwareValues): boolean =>
  values.insurances.length > 0 && values.softwares.length + values.xraySoftwares.length > 0;

/**
 * Which Location Details rows carry the missing-information warning. Contact
 * and Operating Details hold only optional fields.
 */
export const getSectionValidity = (
  values: ILocationSectionValues,
): Record<TLocationSectionId, boolean> => ({
  basicInformation: isBasicInformationValid(values.basicInformation),
  contact: true,
  addressParking: isAddressParkingValid(values.addressParking),
  shiftDetails: isShiftDetailsValid(values.shiftDetails),
  insuranceSoftware: isInsuranceSoftwareValid(values.insuranceSoftware),
  operatingDetails: true,
});
